import { useState } from 'react';
import type { Notice } from '@cornerstone/shared';
import { ModalShell } from './menu';
import { Button, Field } from './ui';
import { useLibraryStore } from '../store/library';
import { useDeviceGroupsStore } from '../store/deviceGroups';
import { ContentOwnerGroupField, applyOwnerGroup, ownerGroupOf } from './ContentOwnerGroupField';
import { describeApiError, toast } from './toast';

// =============================================================================
// NoticeEditModal — 슬라이드 ⋮ → '수정'. 슬라이드명 · 재생시간.
// 캔버스(레이어) 편집은 따로 '디자인 편집'으로 연다 — 미디어 '수정' 모달과 같은 꼴을 맞추기 위해.
// 2026-09-16 대교 결정("폴더 개념 없애자")으로 폴더 선택 필드를 없앴다 — 콘텐츠는 소유 그룹으로만 갈린다.
// 공개/비공개(공개 범위)는 2026-09-17 대교 결정으로 화면에서 없앴다 — 폴더·공통 분류를 걷어낸
// 뒤로 남아 있던 개념의 잔재다. 콘텐츠가 어느 그룹 것인가는 서버가 소유 그룹으로 정한다.
// =============================================================================

export function NoticeEditModal({
  notice,
  canChooseGroup = false,
  onClose,
}: {
  notice: Notice;
  /** 소유 그룹을 옮길 수 있는가 — 조직 Owner·중앙 관리자만. */
  canChooseGroup?: boolean;
  onClose: () => void;
}): JSX.Element {
  const updateNotice = useLibraryStore((s) => s.updateNotice);
  const loadNotices = useLibraryStore((s) => s.loadNotices);
  const [name, setName] = useState(notice.name);
  const groups = useDeviceGroupsStore((s) => s.groups);
  const initialGroup = ownerGroupOf(notice);
  const [ownerGroupId, setOwnerGroupId] = useState(initialGroup ?? '');
  const [duration, setDuration] = useState(notice.durationSec > 0 ? String(notice.durationSec) : '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const trimmed = name.trim();
  const durationNum = Number(duration);
  const durationValid = Number.isFinite(durationNum) && durationNum > 0;
  const canSubmit = trimmed.length > 0 && durationValid && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true); setError(null);
    try {
      // 소유 그룹 먼저(revision 검사), 그다음 이름·재생시간. folderId는 보내지 않는다(2026-09-16).
      const moved = canChooseGroup && initialGroup !== null
        ? await applyOwnerGroup('notice', notice, ownerGroupId) : false;
      const patch: { name?: string; durationSec?: number } = {};
      if (trimmed !== notice.name) patch.name = trimmed;
      if (Math.round(durationNum) !== notice.durationSec) patch.durationSec = Math.round(durationNum);
      if (Object.keys(patch).length) await updateNotice(notice.id, patch);
      if (moved) await loadNotices();
      toast('저장되었습니다.', 'success');
      onClose();
    } catch (e) {
      setError(describeApiError(e));
      setBusy(false);
    }
  };
  const labelCls = 'block font-mono text-[12px] font-medium uppercase tracking-monoWide text-text-dim mb-1.5';
  return (
    // 제출·취소는 고정 푸터로(2026-09-16 검수 P2).
    <ModalShell
      title="슬라이드 수정"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>취소</Button>
          <Button variant="primary" disabled={!canSubmit} onClick={() => void submit()}>{busy ? '저장 중…' : '저장'}</Button>
        </>
      }
    >
      <Field label="슬라이드명 *" value={name} autoFocus onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') void submit(); }} />
      {canChooseGroup && initialGroup !== null && (
        <ContentOwnerGroupField value={ownerGroupId} onChange={setOwnerGroupId} groups={groups} noun="슬라이드" />
      )}
      <label className="block">
        <span className={labelCls}>재생시간(초) *</span>
        <input type="number" min={1} step={1} value={duration} onChange={(e) => setDuration(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') void submit(); }}
          className="w-full h-9 bg-bg border border-line rounded px-2 text-[14px] text-text tabular-nums" aria-label="재생시간(초)" />
        {duration.trim().length > 0 && !durationValid && <p className="mt-1 text-[12px] text-red">재생시간은 1초 이상이어야 합니다.</p>}
      </label>
      {error && <div className="font-mono text-[12px] text-red">{error}</div>}
    </ModalShell>
  );
}
